import { Badge } from "@/components/ui/badge";
import { CONF_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";

export const ConfBadge = ({ level = 1, className }) => {
  const styles = {
    1: "bg-navy/8 text-navy border-navy/20",
    2: "bg-gold/18 text-[hsl(30_88%_30%)] border-gold/45",
    3: "bg-navy text-[hsl(46_96%_66%)] border-navy",
  };
  return (
    <span
      data-testid={`conf-badge-${level}`}
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]",
        styles[level] || styles[1],
        className
      )}
    >
      {CONF_LABELS[level] || "General"}
    </span>
  );
};

export const StateBadge = ({ state }) => {
  const map = {
    draft: "bg-muted text-muted-foreground",
    in_progress: "bg-deepblue text-white",
    submitted: "bg-deepblue/20 text-deepblue",
    validated: "bg-success/22 text-success",
    approved: "bg-success/22 text-success",
    published: "bg-success text-white",
    retired: "bg-muted text-muted-foreground line-through",
    suspended: "bg-terracotta/20 text-terracotta",
    active: "bg-success/22 text-success",
    open: "bg-warning/25 text-[hsl(30_88%_30%)]",
    done: "bg-success/22 text-success",
    validation_pending: "bg-warning/25 text-[hsl(30_88%_30%)]",
    rejected: "bg-terracotta/20 text-terracotta",
    assigned: "bg-navy/10 text-navy",
  };
  return (
    <Badge
      data-testid={`state-badge-${state}`}
      variant="outline"
      className={cn("rounded-full border-transparent text-[10px] uppercase tracking-[0.12em]", map[state] || "bg-muted")}
    >
      {String(state || "").replace(/_/g, " ")}
    </Badge>
  );
};

export const PageHeader = ({ eyebrow, title, description, actions, testid }) => (
  <div data-testid={testid} className="relative">
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div className="max-w-2xl">
        {eyebrow && <p className="label-caps mb-3 text-deepblue">{eyebrow}</p>}
        <h1 className="font-display text-4xl font-normal text-navy sm:text-5xl lg:text-6xl">{title}</h1>
        {description && <p className="mt-5 text-sm leading-relaxed text-muted-foreground md:text-base">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
    <div className="gold-rule mt-9 h-[2px] w-full rounded-full opacity-70" />
  </div>
);

export const Metric = ({ label, value, sub, hint, tone = "default", testid }) => {
  const tint = {
    default: "before:bg-navy",
    danger: "before:bg-terracotta",
    warning: "before:bg-warning",
    success: "before:bg-success",
  }[tone];
  const val = {
    default: "text-navy",
    danger: "text-terracotta",
    warning: "text-[hsl(28_82%_38%)]",
    success: "text-success",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={cn(
        "panel panel-hover relative h-full overflow-hidden p-7 pl-8",
        "before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-['']",
        tint
      )}
    >
      <p className="label-caps">{label}</p>
      <p className={cn("mt-5 whitespace-nowrap font-display text-3xl leading-none tabular-nums sm:text-[2.25rem]", val)}>{value}</p>
      {sub && (
        <p className="mt-2 whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">{sub}</p>
      )}
      {hint && <p className="mt-2.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  );
};

export const EmptyState = ({ title, message, testid, action }) => (
  <div data-testid={testid} className="panel flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
    <div className="mb-2 h-12 w-12 rounded-full border border-gold/40 bg-gradient-to-br from-gold/25 to-teal/20" />
    <p className="font-display text-2xl text-navy">{title}</p>
    <p className="max-w-md text-sm leading-relaxed text-muted-foreground">{message}</p>
    {action}
  </div>
);
